import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { resetPassword } from '../utils/api';

const ResetPasswordPage = () => {
        const [searchParams] = useSearchParams();
        const [password, setPassword] = useState('');
        const [confirmPassword, setConfirmPassword] = useState('');
        const [error, setError] = useState('');
        const [success, setSuccess] = useState(false);
        const [token, setToken] = useState('');
        const navigate = useNavigate();

        useEffect(() => {
                const tokenParam = searchParams.get('token');
                if (!tokenParam) {
                        setError('Invalid or missing reset token');
                } else {
                        setToken(tokenParam);
                }
        }, [searchParams]);

        const handleSubmit = async (e) => {
                e.preventDefault();
                setError('');

                // Validate passwords match
                if (password !== confirmPassword) {
                        setError('Passwords do not match');
                        return;
                }

                // Validate password length
                if (password.length < 8) {
                        setError('Password must be at least 8 characters long');
                        return;
                }

                try {
                        const response = await resetPassword(token, password);
                        if (response.ok) {
                                setSuccess(true);
                                setTimeout(() => {
                                        navigate('/login');
                                }, 3000);
                        } else {
                                const data = await response.json();
                                setError(data.error.message || 'Failed to reset password. Please try again.');
                        }
                } catch (error) {
                        setError(error.message || 'An error occurred. Please try again later.');
                }
        };

        if (success) {
                return (
                        <div className="flex items-center justify-center h-screen bg-bg-page">
                                <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md text-center">
                                        <div className="text-green-600 text-5xl mb-4">✓</div>
                                        <h1 data-testid="reset-success" className="text-2xl font-bold text-gray-900">Password Reset Successful!</h1>
                                        <p className="text-gray-600">
                                                Your password has been updated. You will be redirected to the login page shortly.
                                        </p>
                                </div>
                        </div>
                );
        }

        return (
                <div className="flex items-center justify-center h-screen bg-bg-page">
                        <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">
                                <h1 className="text-2xl font-bold text-center text-gray-900">Reset Your Password</h1>
                                <form className="space-y-6" onSubmit={handleSubmit}>
                                        <div>
                                                <label htmlFor="password" className="text-sm font-medium text-gray-700">
                                                        New Password
                                                </label>
                                                <input
                                                        id="password"
                                                        data-testid="reset-password"
                                                        name="password"
                                                        type="password"
                                                        required
                                                        value={password}
                                                        onChange={(e) => setPassword(e.target.value)}
                                                        className="block w-full px-3 py-2 mt-1 placeholder-gray-400 border border-gray-300 rounded-md shadow-sm appearance-none focus:outline-none focus:ring-brand focus:border-brand sm:text-sm"
                                                        placeholder="Enter new password (min 8 characters)"
                                                        disabled={!token}
                                                />
                                        </div>
                                        <div>
                                                <label htmlFor="confirm-password" className="text-sm font-medium text-gray-700">
                                                        Confirm Password
                                                </label>
                                                <input
                                                        id="confirm-password"
                                                        data-testid="reset-confirm-password"
                                                        name="confirm-password"
                                                        type="password"
                                                        required
                                                        value={confirmPassword}
                                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                                        className="block w-full px-3 py-2 mt-1 placeholder-gray-400 border border-gray-300 rounded-md shadow-sm appearance-none focus:outline-none focus:ring-brand focus:border-brand sm:text-sm"
                                                        placeholder="Confirm new password"
                                                        disabled={!token}
                                                />
                                        </div>
                                        {error && <p data-testid="reset-error" className="text-sm text-red-600">{error}</p>}
                                        <div>
                                                <button
                                                        type="submit"
                                                        data-testid="reset-submit"
                                                        disabled={!token}
                                                        className="flex justify-center w-full px-4 py-2 text-sm font-medium text-white bg-brand border border-transparent rounded-md shadow-sm hover:bg-brand/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand disabled:bg-gray-400 disabled:cursor-not-allowed"
                                                >
                                                        Reset Password
                                                </button>
                                        </div>
                                        <div className="text-center">
                                                <button
                                                        type="button"
                                                        onClick={() => navigate('/login')}
                                                        className="text-sm text-brand hover:text-brand/80"
                                                >
                                                        Back to Login
                                                </button>
                                        </div>
                                </form>
                        </div>
                </div>
        );
};

export default ResetPasswordPage;
